import { useEffect, useState } from 'react'
import { Drawer, Field, StringListInput, notify } from '@/kit/ui'
import { EMPTY_FACTS } from '@/fixtures/data'
import { LOAN_TYPE_LABEL, slugify } from '@/lib/lender'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { useHubStore } from '@/store/hubStore'
import type { KeyFacts, Lender, LoanType, Product } from '@/types'

type Num = 'amountMin' | 'amountMax' | 'tenureMinMonths' | 'tenureMaxMonths' | 'rateMinPa' | 'rateMaxPa' | 'aprMin' | 'aprMax' | 'ageMin' | 'ageMax' | 'minIncomeMonth'

/** Edit or add a product (F1-5): every key fact, the KFS file name, the endpoint behind the product. */
export function ProductDrawer({ lender, product, isOpen, onClose }: { lender: Lender; product: Product | null; isOpen: boolean; onClose: () => void }) {
  const { user } = useCurrentUser()
  const updateProduct = useHubStore((s) => s.updateProduct)
  const addProduct = useHubStore((s) => s.addProduct)
  const [name, setName] = useState('')
  const [loanType, setLoanType] = useState<LoanType>('personal')
  const [facts, setFacts] = useState<KeyFacts>(EMPTY_FACTS)
  const [kfs, setKfs] = useState('')
  const [endpoint, setEndpoint] = useState<string>('')
  const [err, setErr] = useState<string>()

  useEffect(() => {
    if (!isOpen) return
    setName(product?.name ?? '')
    setLoanType(product?.loanType ?? 'personal')
    setFacts(product?.keyFacts ?? EMPTY_FACTS)
    setKfs(product?.kfsFile ?? '')
    setEndpoint(product?.endpointId ?? '')
    setErr(undefined)
  }, [isOpen, product])

  const num = (label: string, k: Num, help?: string) => (
    <Field label={label} help={help}>
      {(id) => <input id={id} type="number" className="input" value={facts[k]} onChange={(e) => setFacts({ ...facts, [k]: Number(e.target.value) })} />}
    </Field>
  )

  const endpoints = [...new Set(lender.workflows.map((w) => w.endpointId))]

  const save = () => {
    if (name.trim().length < 2) return setErr('Give the product a name.')
    if (facts.amountMax <= 0 || facts.tenureMaxMonths <= 0 || facts.rateMaxPa <= 0) return setErr('Amount, tenure and rate are required: they show on the website.')
    const patch: Partial<Product> = { name: name.trim(), loanType, keyFacts: facts, kfsFile: kfs.trim() || null, endpointId: endpoint || null, endpointState: endpoint ? (lender.workflows.find((w) => w.endpointId === endpoint && w.state === 'live') ? 'live' : 'uat') : 'none' }
    if (product) {
      updateProduct(lender.slug, product.slug, patch, user.email, `Edited key facts of “${name.trim()}”`)
      notify.success('Product saved', 'Key facts go live within 60 s.')
    } else {
      const slug = `${lender.slug}.${slugify(name)}`
      if (lender.products.some((p) => p.slug === slug)) return setErr('A product with this name exists.')
      addProduct(lender.slug, { ...(patch as Required<typeof patch>), slug, active: true }, user.email)
      notify.success('Product added', slug)
    }
    onClose()
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={product ? `Edit product · ${product.name}` : `Add product · ${lender.name}`}
      subtitle="Key facts show word for word on the website and in the app."
      size="lg"
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <span className="text-2xs text-red-600">{err}</span>
          <span className="flex gap-2">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={save}>{product ? 'Save' : 'Add product'}</button>
          </span>
        </div>
      }
    >
      <Field label="Product name" required>{(id) => <input id={id} className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Personal loan" />}</Field>
      {!product && <p className="-mt-2 mb-3 text-2xs text-muted">Slug: <code>{lender.slug}.{slugify(name) || '…'}</code> (set once)</p>}
      <Field label="Loan type" help="The website's filters and Apply now">{(id) => (
        <select id={id} className="input" value={loanType} onChange={(e) => setLoanType(e.target.value as LoanType)}>
          {(Object.keys(LOAN_TYPE_LABEL) as LoanType[]).map((k) => <option key={k} value={k}>{LOAN_TYPE_LABEL[k]}</option>)}
        </select>
      )}</Field>
      <h3 className="eyebrow mb-2">Key facts</h3>
      <div className="grid grid-cols-2 gap-3">{num('Amount min (₹)', 'amountMin')}{num('Amount max (₹)', 'amountMax')}</div>
      <div className="grid grid-cols-2 gap-3">{num('Tenure min (months)', 'tenureMinMonths')}{num('Tenure max (months)', 'tenureMaxMonths')}</div>
      <div className="grid grid-cols-2 gap-3">{num('Rate min (% p.a.)', 'rateMinPa')}{num('Rate max (% p.a.)', 'rateMaxPa')}</div>
      <Field label="Processing fee">{(id) => <input id={id} className="input" value={facts.processingFee} onChange={(e) => setFacts({ ...facts, processingFee: e.target.value })} placeholder="2% – 8% + GST" />}</Field>
      <div className="grid grid-cols-2 gap-3">{num('APR min (%)', 'aprMin')}{num('APR max (%)', 'aprMax')}</div>
      <div className="grid grid-cols-3 gap-3">{num('Age min', 'ageMin')}{num('Age max', 'ageMax')}{num('Min income / month (₹)', 'minIncomeMonth')}</div>
      <Field label="Documents" help="Enter to add one">{() => <StringListInput value={facts.documents} onChange={(documents) => setFacts({ ...facts, documents })} placeholder="PAN" />}</Field>
      <Field label="Disbursal">{(id) => <input id={id} className="input" value={facts.disbursal} onChange={(e) => setFacts({ ...facts, disbursal: e.target.value })} placeholder="Same day to 24 hours" />}</Field>
      <Field label="Extra key facts" help="Short points; one per line on the website">{() => <StringListInput value={facts.extra} onChange={(extra) => setFacts({ ...facts, extra })} placeholder="No collateral" />}</Field>
      <h3 className="eyebrow mb-2 mt-2">Documents & endpoint</h3>
      <Field label="KFS document (file name in hub-media)" help="Upload comes with the backend; empty = the lender-level default">{(id) => <input id={id} className="input" value={kfs} onChange={(e) => setKfs(e.target.value)} placeholder="kfs-personal-2026-10.pdf" />}</Field>
      <Field label="Endpoint behind this product" help="None = information only: the website and app show the lender with its web link">
        {(id) => (
          <select id={id} className="input" value={endpoint} onChange={(e) => setEndpoint(e.target.value)}>
            <option value="">none (information only)</option>
            {endpoints.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        )}
      </Field>
    </Drawer>
  )
}
